import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { ModalActionButtonStyles } from "../../Common/styles/modal";
import { DrawerForm } from "../../Common/styles/drawer";
import http from "../../../utils/http";
import { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

type Props = {
  closeDrawer: () => void;
  refreshPage: () => void;
  studyId: string;
};

type SettingsKey =
  | "twoFactorAuth"
  | "selfRegister"
  | "defaultAllowPhoneHealthData"
  | "defaultAllowSmartwatchHealthData";

const SettingCheckbox: React.FC<{
  label: string;
  checked: boolean;
  onChange: () => void;
}> = ({ label, checked, onChange }) => (
  <>
    <FormControlLabel
      componentsProps={{
        typography: { sx: { typography: "subtitle1" } },
      }}
      sx={{ typography: "subtitle1", fontWeight: "regular" }}
      control={<Checkbox checked={checked} onChange={onChange} />}
      label={label}
    />
    <Divider />
  </>
);

const Settings: React.FC<Props> = ({ studyId, closeDrawer, refreshPage }) => {
  const { t } = useTranslation("studies");
  const navigation = useNavigate();
  const [submitLoader, setSubmitLoader] = useState(false);
  const [loadSettings, setLoadSettings] = useState(true);
  const [settings, setSettings] = useState<{
    twoFactorAuth: boolean;
    selfRegister: boolean;
    subjectIdStrategy: string;
    defaultAllowPhoneHealthData: boolean;
    defaultAllowSmartwatchHealthData: boolean;
  }>({
    twoFactorAuth: false,
    selfRegister: false,
    subjectIdStrategy: "incremental_per_site",
    defaultAllowPhoneHealthData: false,
    defaultAllowSmartwatchHealthData: false,
  });

  useEffect(() => {
    if (!studyId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadSettings(true);
        const res: AxiosResponse = await http.get(
          `/study/${studyId}/settings/general`
        );
        const d = res?.data?.data;
        if (cancelled || !d) return;
        setSettings({
          twoFactorAuth: !!d.isMfaEnabled,
          selfRegister: !!d.isParticipantSelfRegistrationEnabled,
          subjectIdStrategy: d.subjectIdStrategy || "incremental_per_site",
          defaultAllowPhoneHealthData: !!d.defaultAllowPhoneHealthData,
          defaultAllowSmartwatchHealthData:
            !!d.defaultAllowSmartwatchHealthData,
        });
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        if (!cancelled) setLoadSettings(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studyId]);

  const handleCheckboxChange = (key: SettingsKey) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [key]: !prevSettings[key],
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitLoader(true);
      const obj = {
        isMfaEnabled: settings.twoFactorAuth,
        isParticipantSelfRegistrationEnabled: settings.selfRegister,
        subjectIdStrategy: settings.subjectIdStrategy,
        defaultAllowPhoneHealthData: settings.defaultAllowPhoneHealthData,
        defaultAllowSmartwatchHealthData:
          settings.defaultAllowSmartwatchHealthData,
      };
      const res: AxiosResponse = await http.put(
        `/study/${studyId}/settings/general`,
        obj
      );
      toastMessage("success", res.data.message);
      closeDrawer();
      refreshPage();
      navigation(`/studies/${studyId}/dashboard`);
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setSubmitLoader(false);
    }
  };

  return (
    <Box sx={DrawerForm}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2.5 }}>
        <Typography variant="subtitle1" color="text.secondary" mb={2}>
          {t("ADD_STUDY_DRAWER.SETTINGS_DESCRIPTION")}
        </Typography>
        <Divider />
        {loadSettings ? (
          <CircularProgress size={28} sx={{ alignSelf: "center", my: 2 }} />
        ) : (
          <>
            <SettingCheckbox
              label={t("ADD_STUDY_DRAWER.SETTINGS_2FA_LABEL")}
              checked={settings.twoFactorAuth}
              onChange={() => handleCheckboxChange("twoFactorAuth")}
            />
            <SettingCheckbox
              label={t("ADD_STUDY_DRAWER.SETTINGS_SELF_REGISTER_LABEL")}
              checked={settings.selfRegister}
              onChange={() => handleCheckboxChange("selfRegister")}
            />
            <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
              {t("ADD_STUDY_DRAWER.SETTINGS_DEFAULT_HEALTH_HINT")}
            </Typography>
            <SettingCheckbox
              label={t("ADD_STUDY_DRAWER.SETTINGS_DEFAULT_PHONE_HEALTH_LABEL")}
              checked={settings.defaultAllowPhoneHealthData}
              onChange={() =>
                handleCheckboxChange("defaultAllowPhoneHealthData")
              }
            />
            <SettingCheckbox
              label={t(
                "ADD_STUDY_DRAWER.SETTINGS_DEFAULT_SMARTWATCH_HEALTH_LABEL"
              )}
              checked={settings.defaultAllowSmartwatchHealthData}
              onChange={() =>
                handleCheckboxChange("defaultAllowSmartwatchHealthData")
              }
            />
          </>
        )}
      </Box>
      <Box sx={{ marginTop: "auto" }}>
        <Divider />
        <Box sx={{ ...ModalActionButtonStyles, mt: 2, mb: 0 }}>
          {!submitLoader && !loadSettings ? (
            <Button variant="contained" onClick={handleSubmit}>
              {t("ADD_STUDY_DRAWER.SUBMIT")}
            </Button>
          ) : (
            <CircularProgress size={25} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Settings;
